import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { Toggle } from '../../ui/Toggle/Toggle';
import { useBarGesture } from './useBarGesture';
import { PERCENT, POWER_STATE } from '../../../lib/device/catalog';
import type { PowerState } from '../../../lib/device/catalog';

export interface DimmableLightControlProps {
  id: string;
  name: string;
  subtext?: string;
  icon: React.ReactNode;
  status: PowerState;
  brightness: number;
  isOnline: boolean;
  onToggle: (id: string) => void;
  onBrightnessChange: (id: string, brightness: number) => void;
  className?: string;
}

export const DimmableLightControl: React.FC<DimmableLightControlProps> = ({
  id,
  name,
  subtext,
  icon,
  status,
  brightness,
  isOnline,
  onToggle,
  onBrightnessChange,
  className = '',
}) => {
  const isOn = status === POWER_STATE.ON;
  const canDim = isOnline && isOn;

  const gesture = useBarGesture({
    enabled: canDim,
    onCommit: (level) => onBrightnessChange(id, level),
  });

  // While the guest drags, the gesture owns the value; otherwise the store does.
  const displayBrightness = isOn ? (gesture.draggingValue ?? brightness) : PERCENT.MIN;

  return (
    <motion.div
      className={`
        bg-[#F8F8F8] rounded-2xl p-4 
        ${!isOnline ? 'opacity-50' : ''}
        ${className}
      `}
      whileHover={isOnline ? { y: -2 } : undefined}
      transition={{ duration: 0.2 }}
    >
      {/* Top Row - Icon, Name, and Toggle */}
      <div className="flex items-center gap-4 mb-4">
        {/* Icon Container */}
        <div 
          className={`
            w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
            ${isOn ? 'bg-[#D4A054]' : 'bg-[#FDF6E9]'}
            transition-colors duration-300
          `}
        >
          <div className={`w-6 h-6 flex items-center justify-center ${isOn ? 'text-white' : 'text-[#D4A054]'}`}>
            {icon}
          </div>
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-[#333333] text-base font-medium truncate">
            {name}
          </h3>
          <motion.p
            key={isOn ? 'on' : 'off'}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className={`text-sm ${isOn ? 'text-[#D4A054]' : 'text-[#666666]'}`}
          >
            {isOn ? `${displayBrightness}%` : 'Off'}
            {!isOnline && ' (Offline)'}
          </motion.p>
          {subtext !== undefined && subtext !== '' && (
            <p className="text-xs text-[#999999] truncate">{subtext}</p>
          )}
        </div>

        {/* Toggle Switch */}
        <Toggle
          checked={isOn}
          onChange={() => onToggle(id)}
          disabled={!isOnline}
        />
      </div>

      {/* Brightness Bar */}
      <div className="flex items-center gap-3 mb-4">
        <Moon className="w-4 h-4 text-[#999999] flex-shrink-0" />
        <div 
          className={`
            flex-1 h-12 bg-[#E8E8E8] rounded-xl overflow-hidden relative cursor-pointer
            ${isOnline && isOn ? 'select-none' : 'cursor-not-allowed'}
          `}
          {...gesture.handlers}
        >
          <div 
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#F5D061] to-[#FFD93D] transition-all duration-150"
            style={{ width: `${displayBrightness}%` }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-medium text-[#666666] z-10">
              {displayBrightness}%
            </span>
          </div>
          {isOnline && isOn && (
            <div 
              className="absolute top-0 bottom-0 w-1 bg-white/80 shadow-lg cursor-ew-resize transition-opacity duration-150"
              style={{ left: `calc(${displayBrightness}% - 4px)`, opacity: 0 }}
            />
          )}
        </div>
        <Sun className="w-4 h-4 text-[#D4A054] flex-shrink-0" />
      </div>
    </motion.div>
  );
};

export default DimmableLightControl;
